import * as React from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import type { ExtraInvoiceItem, ExtraInvoiceItems, InvoiceDetails, ItemList, ItemTypeList } from './models/InvoiceInterfaces';
import JobTabsData from './JobTabs';
import Invoices from './Invoices';
import RunsheetInvoiceItemsPopup from './RunSheetInvoiceItemsPopup';
import ConfirmPopups from './ConfirmPopup';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';

const JobInvoiceData = () => {
    const [loading, setLoading] = React.useState(true);
    const [gridLoading, setGridLoading] = React.useState(true);
    let navigate = useNavigate();
    const { id } = useParams();
    const [gst, setGst] = React.useState(0.1);
    const [siteAddress, setSiteAddress] = React.useState("");
    const [jobStatusId, setJobStatusId] = React.useState(-1);
    const [isEvent, setIsEvent] = React.useState(false);
    const [isThreeDayHire, setIsThreeDayHire] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalRecords, setTotalRecords] = React.useState(0);
    const [getTotalRecords, setGetTotalRecords] = React.useState(true);
    const [items, setItems] = React.useState<ItemList[]>([]);
    const [itemTypes, setItemTypes] = React.useState<ItemTypeList[]>([]);
    const [isEpaJob, setIsEpaJob] = React.useState(false);
    const [getInvoiceList, setGetInvoiceList] = React.useState(false);
    const [invoiceDetails, setInvoiceDetails] = React.useState<InvoiceDetails[]>([]);
    const [invoiceTotalFormatted, setInvoiceTotalFormatted] = React.useState("");
    const [canEditHirePeriod, setCanEditHirePeriod] = React.useState(false);
    const [canCreate, setCanCreate] = React.useState(false);
    const [showInvoiceItemModal, setShowInvoiceItemModal] = React.useState(false);
    const [extraInvoiceItem, setExtraInvoiceItem] = React.useState<ExtraInvoiceItem>({
        invoiceItems: [],
        calloutNotes: "",
        noCalloutInvoiceNotes: ""
    });
    const [showConfirm, setShowConfirm] = React.useState(false);
    const [showConfirmProgress, setShowConfirmProgress] = React.useState(false);
    const [saveDisabled, setSaveDisabled] = React.useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);
        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("ViewJobInvoices");
        if (hasPermission) {
            getInvoiceData();
        } else {
            navigate("/accessdenied");
        }
    }

    const getInvoiceData = async () => {
        try {
            const apiUrl = 'GetInvoiceDetails?JobId=' + id;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                setGst(result.data.gst);
                setSiteAddress(result.data.siteAddress);
                setJobStatusId(result.data.jobStatusId);
                setIsEvent(result.data.isEvent);
                setIsThreeDayHire(result.data.isThreeDayHire);
                setIsEpaJob(result.data.isEpaJob);
                setItemTypes(result.data.itemTypes);
                setItems(result.data.jobItems);
                setCanEditHirePeriod(result.data.canEditHirePeriod);
                setCanCreate(result.data.canCreate);
                setLoading(false);
                setSaveDisabled(false);
                getInvoices();
            }
        } catch (e) {
            toast.error("Error getting data!");
        }     
    }

    React.useEffect(() => {
        if (getInvoiceList) {
            setGridLoading(true);
            getInvoices();
        }
    }, [getInvoiceList]);

    const getInvoices = async () => {
        try {
            var totalInvoices = getTotalRecords ? 0 : totalRecords;
            const apiUrl = 'GetInvoices?JobId=' + id + '&page=' + page + '&totalRecords=' + totalInvoices + '&getTotalRecords=' + getTotalRecords;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var invoices = result.data.invoices;
                for (var i = 0; i < invoices.length; i++) {
                    var invoice = invoices[i];
                    invoice.errors = {};

                    for (var y = 0; y < invoice.items.length; y++) {
                        invoice.items[y].itemIndex = y;
                    }
                }
                setInvoiceDetails(invoices);
                setInvoiceTotalFormatted(result.data.invoiceTotalFormatted);
                setTotalRecords(result.data.totalRecords);
                setPageSize(result.data.pageSize);
                setGridLoading(false);
                setGetTotalRecords(true);
                setGetInvoiceList(false);
            }
        } catch (e) {
            toast.error("Error getting data!");
        }
    }

    const pageChanged = (updatedPage: number) => {
        setPage(updatedPage);
        setGetTotalRecords(false);
        setGetInvoiceList(true);
    }

    const updateInvoiceDetails = (updatedInvoices: InvoiceDetails[]) => {
        setInvoiceDetails(updatedInvoices);
    }

    const save = (invoices: InvoiceDetails[]) => {
        if (invoices.length > 0) {
            saveInvoices(invoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    const saveInvoices = async (invoices: InvoiceDetails[]) => {
        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                customerId: invoices[inv].customerId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var saveInvoice = {
            invoiceDetails: invoiceitems
        };

        try {
            var jsonItem = JSON.stringify(saveInvoice);

            const apiUrl = "SaveInvoiceItems";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                setGetTotalRecords(false);
                setGetInvoiceList(true);
                toast.success("Invoices Saved");
            }
        } catch (e) {
            toast.error("Error saving invoice!");
        }
    }

    const saveHirePeriod = (updatedInvoices: InvoiceDetails[]) => {
        saveInvoiceHirePeriod(updatedInvoices);
    }

    const saveInvoiceHirePeriod = async (invoices: InvoiceDetails[]) => {
        if (invoices.length > 0) {
            var invoiceitems = [];
            for (var inv = 0; inv < invoices.length; inv++) {
                var item = {
                    jobId: invoices[inv].jobId,
                    invoiceItems: invoices[inv].items.filter(i => i.isDirty)
                }
                invoiceitems.push(item);
            }

            var saveInvoice = {
                invoiceDetails: invoiceitems
            };

            try {
                var jsonItem = JSON.stringify(saveInvoice);

                const apiUrl = "SaveInvoiceItemHirePeriod";
                var result = await PostQuery(apiUrl, jsonItem);
                if (result.error) {
                    toast.error("Error saving data!");
                } else {
                    setGetTotalRecords(false);
                    setGetInvoiceList(true);
                    toast.success("Invoice Saved");
                }
            } catch (e) {
                toast.error("Error saving invoice!");
            }
        } else {
            toast.info("No invoices have been changed");
        }
    }

    //ADD INVOICE ITEM
    const addInvoiceItems = (e: any) => {
        e.preventDefault();

        //add blank invoice item
        var invItems = [...extraInvoiceItem.invoiceItems];
        var invItem = addBlankInvoiceItem();
        invItems.push(invItem);

        setShowInvoiceItemModal(true);
        setExtraInvoiceItem(prevState => ({ ...prevState, invoiceItems: invItems }));
    }

    const addBlankInvoiceItem = () => {
        var invItem: ExtraInvoiceItems = {
            id: 0,
            rowIndex: 0,
            active: true,
            itemId: -1,
            itemTypeId: -1,
            scheduleId: 0,
            jobQuoteItemId: 0,
            description: "",
            rate: 0,
            gst: 0,
            amount: 0
        };
        return invItem;
    }

    const hideInvoiceModal = () => {
        let invItem = resetExtraInvoiceItem();
        setExtraInvoiceItem(invItem);
        setShowInvoiceItemModal(false);
    }

    const updateInvoiceItems = (noCalloutInvoiceNotes: string, calloutNotes: string, invoiceItems: ExtraInvoiceItems[]) => {
        setExtraInvoiceItem(prevState => ({ ...prevState, noCalloutInvoiceNotes: noCalloutInvoiceNotes, calloutNotes: calloutNotes, invoiceItems: invoiceItems }));
    }

    const saveInvoiceItems = () => {
        saveExtraInvoiceItems();
    }

    const saveExtraInvoiceItems = async () => {
        var extraInvItems = [...extraInvoiceItem.invoiceItems.filter(ii => ii.active)];
        extraInvItems.pop();

        var invItems = {
            jobId: id,
            items: extraInvItems
        };

        try {
            var jsonItem = JSON.stringify(invItems);

            const apiUrl = "SaveExtraInvoiceItems";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                toast.success("Extra Item Invoice Created");

                //reset
                let invItem = resetExtraInvoiceItem();
                setExtraInvoiceItem(invItem);
                setShowInvoiceItemModal(false);
                setGetTotalRecords(true);
                setGetInvoiceList(true);
            }
        } catch (e) {
            toast.error("Error saving job!");
        }
    }

    const resetExtraInvoiceItem = () => {
        var invoiceItem: ExtraInvoiceItem = {
            invoiceItems: [],
            calloutNotes: "",
            noCalloutInvoiceNotes: ""
        };

        return invoiceItem;
    }
    //END ADD INVOICE ITEM

    //Re-create Initial Invoice
    const recreateInvoice = (e: any) => {
        e.preventDefault();
        setShowConfirm(true);
    }

    const hideConfirm = () => {
        setShowConfirm(false);
        setShowConfirmProgress(false);
        setSaveDisabled(false);
    }

    const saveRecreateInitialInvoice = async () => {
        var recreate = {
            jobId: id
        };

        try {
            var jsonItem = JSON.stringify(recreate);

            const apiUrl = "RecreateInitialInvoice";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    setShowConfirm(false);
                    toast.success("Initial Invoice recreated successfully");
                    setGetTotalRecords(true);
                    setGetInvoiceList(true);
                }
            }
        } catch (e) {
            toast.error("Error re-creating initial invoice!");
        }
    }
    //End Re-create Initial Invoice

    //Create a $5K progress invoice for EPA
    const createProgressInvoice = (e: any) => {
        e.preventDefault();

        if (saveDisabled) {
            return;
        }
        setSaveDisabled(true);
        setShowConfirmProgress(true);
    }

    const saveProgressInvoice = async () => {
         try {
            var progress = {
                jobId: id
            };

            var jsonItem = JSON.stringify(progress);

            const apiUrl = "CreateProgressInvoice";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    setShowConfirmProgress(false);

                    toast.success("Progress Invoice created successfully");
                    setGetTotalRecords(true);
                    setGetInvoiceList(true);
                }
            }
        } catch (e) {
            toast.error("Error saving progress invoice!");
        }
    }
    //End Create a Progress Invoice

    const tabChanged = (url: string) => {
        navigate(url);
    }

    var invoiceList = <Invoices invoiceDetails={invoiceDetails} invoiceTotalFormatted={invoiceTotalFormatted} showCustType={false} showJobInfo={false} showQuoteNo={false} showHirePeriod={true} showStatus={true} showStartDate={false} showPaidDate={true} showPaid={false} showSend={false} showReckonInvNo={true} showRemove={false} showSentTo={true} showError={true} showSent={false} canEdit={false} canEditHirePeriod={canEditHirePeriod} canSend={false} canUpdatePaid={false} page={page} pageSize={pageSize} totalRecords={totalRecords} pageChanged={pageChanged} beforeValid={updateInvoiceDetails} afterValid={save} saveHirePeriod={saveHirePeriod} />
    let addInvoice = <RunsheetInvoiceItemsPopup showItems={true} showModal={showInvoiceItemModal} items={items} itemTypes={itemTypes} gst={gst} invoiceItem={extraInvoiceItem} invoiceItemModalType={1} hideModal={hideInvoiceModal} updateData={updateInvoiceItems} saveModal={saveInvoiceItems} />;
    let confirmRecreate = <ConfirmPopups heading="Cancel and Re-create Initial Invoice" text="This will cancel all existing initial invoices and re-create them based on what items have been installed. Continue?" showConfirmModal={showConfirm} hideConfirmModal={hideConfirm} saveConfirmModal={saveRecreateInitialInvoice} />
    let confirmProgress = <ConfirmPopups heading="Create a $5K Progress Invoice" text="This will create a $5,000 progress invoice for the customer. Continue?" showConfirmModal={showConfirmProgress} hideConfirmModal={hideConfirm} saveConfirmModal={saveProgressInvoice} />
    const jobTab = <JobTabsData jobId={Number(id)} siteAddress={siteAddress} tab={"invoices"} tabChanged={tabChanged} />;

    const renderDetails = (
        <div>
            <div className="static-modal">
                {addInvoice}
                {confirmRecreate}
                {confirmProgress}
            </div>
            {jobTab}
            <div className={canCreate && jobStatusId != 7 ? "colAlignRight marginBottom10" : "hidden"}>
                <button className={isEvent || isThreeDayHire ? "defaultbutton defaultbutton marginRight10" : "hidden"} type='button' title="Cancel exisiting initial invoice and create a new initial invoice based on what has been installed" onClick={recreateInvoice}>Re-create Initial Invoice</button>
                <button className={isEpaJob ? "defaultbutton defaultbutton marginRight10" : "hidden"} type='button' title="Create a $5K progress invoice for the customer" onClick={createProgressInvoice} disabled={saveDisabled}>Create $5K Progress Invoice</button>
                <button className="defaultbutton defaultbutton__small" type='button' title="Create an invoice for extra charges for the job/item" onClick={addInvoiceItems}>Add New</button>
            </div>
            <div className={gridLoading ? "" : "hidden"}>Loading Invoices...</div>
            <div className={gridLoading ? "hidden" : "" }>
                {invoiceList}
            </div>
        </div>
    )

    let contents = loading
        ? <p><em>Loadingxx...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );

}

export default JobInvoiceData;